import {
  CalendarOutlined,
  HourglassOutlined,
  VideoCameraOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import {Button, Image, Result, Space, Spin} from "antd";
import {useParams} from "react-router-dom";
import {LoadingSpinner} from "../../../components/icons/icons";
import {PublicLayout} from "../../../layouts/public";
import {useGetCustomerBooking} from "../../../react-query-hooks/booking";
import {StyledFlexContainer} from "../../../styles/email-verification/email-verification";
import {
  StyledJoinCallBox,
  StyledJoinCallExtraInfo,
} from "../../../styles/meetings/meetings";
import {handleMeetingLink} from "../../../utils/meeting";

const JoinMeetingPage = () => {
  const {reference} = useParams();

  // KNOWN v2 GAP, not something this page can paper over:
  //
  // v1 served this page from `customer-booking/:reference/guest_user`, a public
  // route that returned the whole booking graph. v2 has no equivalent. The nearest
  // route, `customer/bookings/:reference/join`, (a) sits behind CustomerAuthGuard,
  // so a guest arriving from a reminder email with no customer session gets a 401,
  // and (b) answers with {token, roomId, videoPlatform} only - the service name,
  // coach name, date, timeslot and timezone rendered below are stubbed to empty by
  // the join normalizer, and the date therefore reads "Invalid Date".
  //
  // Only the Google Meet polling below survives intact, since it reads just
  // videoPlatform and booking.roomId. Fixing the rest needs a public booking-join
  // route on the backend, the session equivalent of `public/live-event/join/:reference`.
  const getCustomerBookingQuery = useGetCustomerBooking(
    {reference},
    {
      refetchInterval: (data) => {
        if (
          data?.videoPlatform?.toLowerCase() === "googlemeet" &&
          !data?.booking?.roomId
        ) {
          return 5000;
        }
        return false;
      },
    }
  );

  if (getCustomerBookingQuery.isLoading) {
    return (
      <PublicLayout>
        <div
          style={{
            height: "80vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }
  if (getCustomerBookingQuery.isError) {
    return (
      <PublicLayout>
        <Result
          status="error"
          title="An Error Occurred"
          subTitle={getCustomerBookingQuery.error.message}
          extra={[
            <Button
              onClick={() => getCustomerBookingQuery.refetch}
              type="default"
              key="console"
              size="small"
            >
              Try again
            </Button>,
          ]}
        />
      </PublicLayout>
    );
  }

  const callDate = new Date(
    Number(getCustomerBookingQuery.data.booking.booking_year),
    Number(getCustomerBookingQuery.data.booking.booking_month) - 1,
    Number(getCustomerBookingQuery.data.booking.booking_date)
  ).toDateString();

  const disableButton = () => {
    if (!getCustomerBookingQuery.data.bookingToken) {
      return true;
    }
    if (
      getCustomerBookingQuery.data.videoPlatform.toLowerCase() === "googlemeet" &&
      !getCustomerBookingQuery.data.booking.roomId
    ) {
      return true;
    }
    return false;
  };

  const handleMeetingPlatform = (
    meetingPlatform: "dailyCo" | "videoSdk" | "googleMeet" | "custom"
  ) => {
    if (meetingPlatform.toLowerCase() === "dailyco") {
      return `${getCustomerBookingQuery.data.dailyCoUrl}?t=${getCustomerBookingQuery.data.bookingToken}`;
    }
    if (meetingPlatform.toLowerCase() === "googlemeet") {
      return getCustomerBookingQuery.data.booking.roomId ?? "";
    }
    return handleMeetingLink("guest", {
      token: getCustomerBookingQuery.data.bookingToken,
      roomId: getCustomerBookingQuery.data.booking.roomId ?? "",
      firstName: getCustomerBookingQuery.data.booking.first_name,
      lastName: getCustomerBookingQuery.data.booking.last_name,
      meetingTitle: getCustomerBookingQuery.data.service.name,
    });
  };

  return (
    <PublicLayout>
      <StyledFlexContainer>
        <Spin
          spinning={getCustomerBookingQuery.isFetching}
          indicator={LoadingSpinner}
          tip="Updating..."
        >
          <StyledJoinCallBox>
            <div>
              <Image
                preview={false}
                //   width={"100%"}
                src="/images/join-meeting.png"
                alt="meeting"
              />
            </div>
            <p className="join-meeting-title">
              "{getCustomerBookingQuery.data?.service.name}" with{" "}
              {getCustomerBookingQuery.data?.mentor.firstName}{" "}
              {getCustomerBookingQuery.data?.mentor.lastName}
            </p>
            <div>
              <Button
                size="small"
                block
                disabled={disableButton()}
                type="primary"
                htmlType="button"
                target="_blank"
                href={
                  disableButton()
                    ? undefined
                    : handleMeetingPlatform(getCustomerBookingQuery.data.videoPlatform)
                }
              >
                Join video call now
              </Button>
            </div>
            {disableButton() &&
              (getCustomerBookingQuery.data?.videoPlatform?.toLowerCase() ===
                "googlemeet" && !getCustomerBookingQuery.data?.booking?.roomId ? (
                <p>Meeting link is being prepared, please wait...</p>
              ) : (
                <p>
                  You will be able to join this video call 10 minutes before the start
                  time
                </p>
              ))}

            <div>
              <StyledJoinCallExtraInfo>
                {/* <div className="call-card-extra-info-item">
                <Space size={10}>
                  <DollarOutlined style={{color: "#222222"}} />
                  <span className="call-card-extra-info-item-value">
                  </span>
                </Space>
              </div> */}
                <div className="call-card-extra-info-item">
                  <Space size={10}>
                    <CalendarOutlined style={{color: "#222222"}} />
                    <span className="call-card-extra-info-item-value">
                      {getCustomerBookingQuery.data?.booking.timeslot}, {callDate}
                    </span>
                  </Space>
                </div>
                <div className="call-card-extra-info-item">
                  <Space size={10}>
                    <HourglassOutlined style={{color: "#222222"}} />
                    <span className="call-card-extra-info-item-value">
                      {getCustomerBookingQuery.data?.service.callDuration} minutes
                    </span>
                  </Space>
                </div>
              </StyledJoinCallExtraInfo>
              <StyledJoinCallExtraInfo style={{marginTop: "2.5rem"}}>
                <div className="call-card-extra-info-item">
                  <Space size={10}>
                    <GlobalOutlined style={{color: "#222222"}} />
                    <span className="call-card-extra-info-item-value">
                      {getCustomerBookingQuery.data?.booking.user_timezone}
                    </span>
                  </Space>
                </div>
                <div className="phone-number-extra-details">
                  <div className="call-card-extra-info-item">
                    <Space size={10}>
                      <>
                        <VideoCameraOutlined style={{color: "#222222"}} />
                        <span className="call-card-extra-info-item-value">
                          Video call
                        </span>
                      </>
                    </Space>
                  </div>
                </div>
              </StyledJoinCallExtraInfo>
            </div>
          </StyledJoinCallBox>
        </Spin>
      </StyledFlexContainer>
    </PublicLayout>
  );
};

export default JoinMeetingPage;
